import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getWithdrawalLimit } from '../../api/walletApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
};
const th = { color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' };

const WithdrawalLimits = () => {
  const navigate = useNavigate();
  const [limitData, setLimitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getWithdrawalLimit()
      .then((res) => setLimitData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load withdrawal limits.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <button
          onClick={() => navigate('/account-status')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Account Status
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Withdrawal Limits</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
          Your withdrawal limit grows as you activate slots and bring in active direct referrals.
        </div>

        {error && (
          <div style={{ color: '#ff6666', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: cyan }} />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <Row className="g-3 mb-4">
              {[
                { label: 'Total Limit', value: limitData?.totalLimit, color: '#ffaa44' },
                { label: 'Used Limit', value: limitData?.usedLimit, color: '#ff6666' },
                { label: 'Balance Limit', value: limitData?.availableLimit, color: '#66ff99' },
              ].map(({ label, value, color }) => (
                <Col xs={12} md={4} key={label}>
                  <Card style={cardDark} className="p-4 h-100">
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>{label}</div>
                    <div>
                      <span style={{ fontSize: 28, fontWeight: 800, color }}>{(value ?? 0).toFixed(2)}</span>
                      <span style={{ color: '#aaa', fontSize: 13, marginLeft: 8 }}>USDT</span>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Slot-wise breakdown */}
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Slot-wise Limit Breakdown</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                    <th style={{ ...th, whiteSpace: 'nowrap' }}>Slot</th>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <th key={s.planId} style={{ ...th, whiteSpace: 'nowrap', textAlign: 'center' }}>{s.planName}</th>
                    ))}
                    <th style={{ ...th, whiteSpace: 'nowrap', textAlign: 'center', color: '#ffaa44' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Upgrade</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ color: '#66ff99', fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {s.amount} USDT
                      </td>
                    ))}
                    <td style={{ padding: '14px 20px', border: 'none', textAlign: 'center' }}>—</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Direct Referrals</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ color: s.directCount > 0 ? cyan : '#555', fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center' }}>
                        {s.directCount > 0 ? s.directCount : '—'}
                      </td>
                    ))}
                    <td style={{ color: cyan, fontWeight: 700, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center' }}>
                      {limitData?.totalActiveDirectReferrals ?? 0}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>Limit Received</td>
                    {(limitData?.slotBreakdown || []).map((s) => (
                      <td key={s.planId} style={{ color: s.limitReceived > 0 ? '#ffaa44' : '#555', fontWeight: 700, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {s.limitReceived > 0 ? `${s.limitReceived.toFixed(2)} USDT` : '—'}
                      </td>
                    ))}
                    <td style={{ color: '#ffaa44', fontWeight: 800, fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {(limitData?.slotBreakdown || []).reduce((sum, s) => sum + s.limitReceived, 0).toFixed(2)} USDT
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {/* Limit log table */}
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12, marginTop: 28 }}>Withdrawal Limit Log</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                    <th style={th}>Comments</th>
                    <th style={th}>Trigger</th>
                    <th style={th}>Date</th>
                    <th style={th}>Limit Added</th>
                  </tr>
                </thead>
                <tbody>
                  {(limitData?.logs || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ color: '#555', textAlign: 'center', padding: '32px', border: 'none' }}>
                        No withdrawal limit logs yet
                      </td>
                    </tr>
                  ) : (
                    limitData.logs.map((log) => (
                      <tr key={log._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ color: '#fff', fontSize: 13, padding: '14px 20px', border: 'none' }}>
                          {log.comments}
                        </td>
                        <td style={{ color: '#888', fontSize: 12, padding: '14px 20px', border: 'none' }}>
                          {log.triggerType === 'self_activation' ? 'Self Activation' : 'Referral Activation'}
                        </td>
                        <td style={{ color: '#888', fontSize: 12, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                          {fmtIST(log.createdAt)}
                        </td>
                        <td style={{ color: '#ffaa44', fontWeight: 700, fontSize: 14, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                          +{log.limitAdded.toFixed(2)} USDT
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default WithdrawalLimits;
